/**
 * A feedback round's invitation to a Symphony user, answered INSIDE the chat (`FeedbackInviteCard`) instead of on the
 * public page a link would open in another tab.
 *
 * The VMR outreach service sends a Symphony user's invitation on the APP channel: an ASSISTANT row in the invitee's own
 * thread with the owner, whose text is the invitation and its link, and whose `metadata.feedbackInviteCard` carries the
 * round id, the invitee's OWN link token and the design's public face (VMR `FeedbackInvite.card`). The card reads the
 * round live and answers through `/api/feedback-invite` → native-data `feedback-invite-get` / `feedback-invite-submit`
 * (ai-scheduling-assistant-server `handlers/feedbackInvite.ts`), the same actions the phones post, so one person's
 * answer is the same on web, iOS and Android.
 *
 * The token is a credential: it rides request bodies only, never a URL, and nothing logs it.
 *
 * Pure (no React, no runtime imports), so a unit test does not import the app's dependency graph. Mirrors iOS
 * `FeedbackInviteCardData` and Android `FeedbackInviteCardData`.
 */
import { isWellFormedToken } from './share-card-view';
import { feedbackLength, MAX_FEEDBACK_CHARS, type FeedbackView } from './feedback-page';

export interface FeedbackInviteCardData {
  roundId: string;
  token: string;
  senderName?: string;
  assetTitle?: string;
  assetPreviewUrl?: string;
  assetMediaUrl?: string;
  assetType?: string;
}

const text = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value.trim() : undefined);
const https = (value: unknown): string | undefined => {
  const url = text(value);
  return url && /^https:\/\//i.test(url) ? url : undefined;
};

function objectOf(value: unknown): Record<string, unknown> | undefined {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/** The card a row carries, or null. Fail-closed on the round id and a well-formed token; every other field drops itself. */
export function readFeedbackInviteCard(metadata: unknown): FeedbackInviteCardData | null {
  const card = objectOf((metadata as { feedbackInviteCard?: unknown } | null | undefined)?.feedbackInviteCard);
  if (!card) return null;
  const roundId = text(card.roundId);
  const token = text(card.token);
  if (!roundId || !token || !isWellFormedToken(token)) return null;
  const data: FeedbackInviteCardData = { roundId, token };
  const senderName = text(card.senderName);
  if (senderName) data.senderName = senderName;
  const assetTitle = text(card.assetTitle);
  if (assetTitle) data.assetTitle = assetTitle;
  const assetPreviewUrl = https(card.assetPreviewUrl);
  if (assetPreviewUrl) data.assetPreviewUrl = assetPreviewUrl;
  const assetMediaUrl = https(card.assetMediaUrl);
  if (assetMediaUrl) data.assetMediaUrl = assetMediaUrl;
  const assetType = text(card.assetType)?.toLowerCase();
  if (assetType) data.assetType = assetType;
  return data;
}

/** What the card shows before (and if) its live read answers: the row's own facts. */
export function firstPaintView(card: FeedbackInviteCardData): Pick<FeedbackView, 'senderName' | 'assetTitle' | 'previewUrl' | 'mediaUrl'> {
  return {
    senderName: card.senderName ?? '',
    previewUrl: card.assetPreviewUrl ?? '',
    ...(card.assetTitle ? { assetTitle: card.assetTitle } : {}),
    ...(card.assetMediaUrl ? { mediaUrl: card.assetMediaUrl } : {}),
  };
}

/** What the card's full-screen view opens: the video when the round has one, else the still. Null without either. */
export function feedbackInviteFullscreenMedia(view: Partial<Pick<FeedbackView, 'previewUrl' | 'mediaUrl' | 'assetTitle'>>): { url: string; kind: 'image' | 'video'; name: string } | null {
  const name = view.assetTitle || 'Design';
  if (view.mediaUrl) return { url: view.mediaUrl, kind: 'video', name };
  if (view.previewUrl) return { url: view.previewUrl, kind: 'image', name };
  return null;
}

/** The bridge's answer, as the card acts on it. */
export type FeedbackInviteOutcome =
  | { kind: 'ok'; feedback: FeedbackView }
  | { kind: 'unavailable' }
  | { kind: 'rate_limited' }
  | { kind: 'failed' };

/** A status and body from `/api/feedback-invite` → the outcome. Anything unexpected is a failure to retry. */
export function feedbackInviteOutcome(status: number, body: unknown): FeedbackInviteOutcome {
  const payload = (body ?? {}) as { feedback?: FeedbackView; error?: unknown };
  if (status === 200 && payload.feedback && typeof payload.feedback.previewUrl === 'string') return { kind: 'ok', feedback: payload.feedback };
  if (status === 404) return { kind: 'unavailable' };
  if (status === 429) return { kind: 'rate_limited' };
  return { kind: 'failed' };
}

/** Send is offered for a reaction or some words, never past the service's 500 characters (counted as a reader counts). */
export function canSendFeedback(answer: { text: string; reaction?: string }, sending: boolean): boolean {
  const length = feedbackLength(answer.text.trim());
  return !sending && length <= MAX_FEEDBACK_CHARS && (length > 0 || !!answer.reaction);
}

export type FeedbackInviteRequest =
  | { action: 'get'; token: string }
  | { action: 'submit'; token: string; text: string; reaction?: string };

/** `/api/feedback-invite`'s body → the native-data action and its allowlisted params, or null to refuse. */
export function feedbackInviteBridgeCall(body: unknown): { action: 'feedback-invite-get' | 'feedback-invite-submit'; params: Record<string, unknown> } | null {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const token = typeof b.token === 'string' ? b.token : '';
  if (!isWellFormedToken(token)) return null;
  if (b.action === 'get') return { action: 'feedback-invite-get', params: { token } };
  if (b.action !== 'submit') return null;
  const answer = typeof b.text === 'string' ? b.text : '';
  const reaction = typeof b.reaction === 'string' && b.reaction ? b.reaction : undefined;
  return { action: 'feedback-invite-submit', params: reaction ? { token, text: answer, reaction } : { token, text: answer } };
}
